import { Preferences } from '@capacitor/preferences';
import * as storage from './storage';

const moveIsAppVisited = () => {
  if (window.localStorage.getItem('isAppVisited') !== null) return;

  const reduxStateString = window.localStorage.getItem('persist:root');
  if (reduxStateString === null) return;

  const reduxState = JSON.parse(reduxStateString);
  const persistedApp = JSON.parse(reduxState.persistedApp);

  if (persistedApp.isAppVisited) {
    window.localStorage.setItem('isAppVisited', 'true');
  }
};

const moveHasFeedbackBeenClosed = () => {
  if (window.localStorage.getItem('hasFeedbackBeenClosed') !== null) return;

  const reduxStateString = window.localStorage.getItem('persist:root');
  if (reduxStateString === null) return;

  const reduxState = JSON.parse(reduxStateString);
  const persistedApp = JSON.parse(reduxState.persistedApp);

  if (persistedApp.hasFeedbackBeenClosed) {
    window.localStorage.setItem('hasFeedbackBeenClosed', 'true');
  }
};

const moveWatchlist = () => {
  if (window.localStorage.getItem('watchlist') !== null) return;

  const reduxStateString = window.localStorage.getItem('persist:root');
  if (reduxStateString === null) return;

  const reduxState = JSON.parse(reduxStateString);
  const watchlistString = reduxState.watchlist;

  window.localStorage.setItem('watchlist', watchlistString);
};

const resetInvestmentsCache = () => {
  const investmentsCacheString = window.localStorage.getItem('investmentsCache') || 'null';
  if (Array.isArray(JSON.parse(investmentsCacheString))) {
    window.localStorage.removeItem('investmentsCache');
  }
};

const migratePortfoliosToIncludeSymbols = async () => {
  const portfolios = await storage.getPortfolios();

  // Check if any portfolios need updating
  const needsUpdate = portfolios.some((p) => !p.symbols);

  if (!needsUpdate) return; // No portfolios need updating

  // Update all portfolios to include symbols array
  const updatedPortfolios = portfolios.map((portfolio) => {
    if (!portfolio.symbols) {
      // Extract symbols from allocations if they exist
      const symbols = portfolio.allocations ? portfolio.allocations.map((alloc) => alloc.symbol) : [];

      return {
        ...portfolio,
        symbols,
      };
    }
    return portfolio;
  });

  await storage.set('portfolios', updatedPortfolios);
};

export const migrateWatchlistToPortfolio = async () => {
  // Skip migration - onboarding now adds symbols directly to portfolios
  // This prevents race conditions and conflicts with the new portfolio-first approach
  console.log('Skipping legacy watchlist migration - using portfolio-first approach');
  return;

  // Legacy migration code disabled to prevent conflicts with onboarding
  // const savedGeneral = await storage.get<SavedGeneral>('GeneralStore');
  // const watchlist = savedGeneral?.watchlist || {};
  // const watchlistSymbols = Object.keys(watchlist).filter((symbol) => watchlist[symbol]);
  //
  // if (!watchlistSymbols.length) return; // No watchlist to migrate
  //
  // const portfolios = await storage.getPortfolios();
  // if (portfolios.some((p) => p.title === 'My Watchlist')) return; // Already migrated
  //
  // const defaultPortfolio: storage.Portfolio = {
  //   id: uuidv4(),
  //   title: 'My Watchlist',
  //   strategy: 'Default watchlist',
  //   symbols: watchlistSymbols,
  //   allocations: [],
  // };
  //
  // await storage.set('portfolios', [...portfolios, defaultPortfolio]);
  // General.update((s) => {
  //   s.watchlist = {}; // Clear the old watchlist
  // });
};

/**
 * Purge stale `portfolioViewed-{id}` keys from localStorage.
 * These were write-only (never read) and accumulated unboundedly — one key per
 * portfolio per view — eventually filling localStorage and causing
 * QuotaExceededError on some devices.  The writes were removed but existing
 * keys remain on users' devices, so we clean them up proactively.
 */
const purgePortfolioViewedKeys = () => {
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('portfolioViewed-')) {
        keysToRemove.push(key);
      }
    }
    if (keysToRemove.length > 0) {
      keysToRemove.forEach((key) => localStorage.removeItem(key));
      console.log(`Purged ${keysToRemove.length} stale portfolioViewed-* keys`);
    }
  } catch {
    // Ignore errors (e.g., SecurityError in Safari Private Browsing)
  }
};

export const runAllMigrations = async () => {
  purgePortfolioViewedKeys();
  moveIsAppVisited();
  moveHasFeedbackBeenClosed();
  moveWatchlist();
  resetInvestmentsCache();
  await migrateWatchlistToPortfolio();
  await migratePortfoliosToIncludeSymbols();

  // Safely migrate Capacitor Preferences to prevent transaction errors
  try {
    await Preferences.migrate();
  } catch (error) {
    console.warn('Preferences migration failed, continuing app startup:', error);
    // Continue app startup even if migration fails
    // This prevents the "Attempt to delete range from database without an in-progress transaction" error
  }
};
